import type { Auth, LoginForm, LoginResponse, RegisterForm } from "@src/types"
import { configMedia } from "@src/utils/constants"
import httpChat from "@src/services/HttpChat"
import api from "@services/api"

import * as LegacyTypes from "@pages/LegacyProfiles/LegacyProfiles.types"
import * as TQuestions from "@pages/Questions/Questions.types"
import * as ChatTypes from "@pages/Chat/Chat.types"
import * as UsersTypes from "@pages/Users/Users.types"

export const apiService = {
  // Auth endpoints
  auth: {
    googleLogin: () =>
      api.post<LoginGoogleResponse | ValidationError | GenericError>("/google_auth", {
        from: "web",
      }),
    googleRedirect: (code: string) =>
      api.post<BaseResponse<LoginResponse> | ValidationError | GenericError>("/auth/google/redirect", {
        code,
      }),
    login: (credentials: LoginForm) =>
      api.post<BaseResponse<LoginResponse> | ValidationError | GenericError>("/auth/login", credentials),
    loginAdmin: (credentials: LoginForm) =>
      api.post<BaseResponse<LoginResponse> | ValidationError | GenericError>(
        "/admin-auth/login",
        credentials,
      ),
    register: (userData: RegisterForm) => api.post<BaseResponse<BaseErrors>>("/auth/register", userData),
    logout: () => api.post<GenericError>("/auth/logout"),
    verifyEmail: (token: string) => api.post("/auth/verify-email", { token }),
    forgotPassword: (email: string) => api.post("/auth/forgot-password", { email }),
    resetPassword: (token: string, password: string) => api.post("/auth/reset-password", { token, password }),
    refreshToken: () => api.post("/auth/refresh-token"),
    resendEmail: () => api.post("/email/resend"),
    me: () => api.get("/auth/user"),
    updateProfile: (payload: Partial<IProfileForm>) =>
      api.post<BaseResponse<IProfileForm> | ValidationError | GenericError>("/update-user-info", payload),
    updatePassword: (payload: Partial<IProfileChangePasswordForm>) =>
      api.post<BaseResponse<IProfileChangePasswordForm> | ValidationError | GenericError>(
        "/change-password",
        payload,
      ),
    uploadAvatar: (file: File) =>
      api.post<BaseResponse<Auth>>("/upload-avatar-picture", { file }, configMedia),
    resendEmailVerification: () => api.post<BasicResponse>("/email/resend"),
  },
  legacy: {
    getProfiles: (params?: Record<string, any>) =>
      api.get<ResponsePagination<LegacyProfile[]>>("/assistants", { params }),
    createProfile: (profileData: any) =>
      api.post<BaseResponse<LegacyProfile> | ValidationError | GenericError>("/assistants", profileData),
    updateProfile: (uuid: string, profileData: any) =>
      api.put<BaseResponse<LegacyProfile> | ValidationError | GenericError>(
        `/assistants/${uuid}`,
        profileData,
      ),
    updateAvatar: (payload: any) =>
      api.post<BaseResponse<LegacyProfile> | ValidationError | GenericError>(
        `/set-assistant-avatar`,
        payload,
        configMedia,
      ),
    deleteProfile: (uuid: string) => api.delete(`/assistants/${uuid}`),
  },
  // Questions endpoints
  questions: {
    getCollection: (params: TQuestions.IQueryParams) =>
      api.get<TQuestions.ResponseQuestions>(`/assistant-questions`, { params }),
    create: (uuid: string, payload: any) => api.post(`/assistant-questions`, payload),
    // update: (id: string, questionData: any) => api.put(`/questions/${id}`, questionData),
    // delete: (id: string) => api.delete(`/questions/${id}`),
  },
  answers: {
    getCollection: (params: TQuestions.IQParamsAnswers) => httpChat.get(`/answers`, { params }),
    getAnswer: (id: string) => httpChat.get(`/answers/${id}`),
    create: (payload: any) => httpChat.post(`/answers`, payload),
    update: (id: string, payload: any) => httpChat.put(`/answers/${id}`, payload),
    delete: (id: string) => httpChat.delete(`/answers/${id}`),
  },
  // Conversations endpoints
  conversations: {
    getConversations: (legacyId: string) => api.get(`/legacy/${legacyId}/conversations`),
    createConversation: (legacyId: string, conversationData: any) =>
      api.post(`/legacy/${legacyId}/conversations`, conversationData),
    updateConversation: (id: string, conversationData: any) =>
      api.put(`/conversations/${id}`, conversationData),
    deleteConversation: (id: string) => api.delete(`/conversations/${id}`),
  },
  // Messages endpoints
  messages: {
    getMessages: (legacyId: string) => api.get(`/legacy/${legacyId}/messages`),
    createMessage: (legacyId: string, messageData: any) =>
      api.post(`/legacy/${legacyId}/messages`, messageData),
    updateMessage: (id: string, messageData: any) => api.put(`/messages/${id}`, messageData),
    deleteMessage: (id: string) => api.delete(`/messages/${id}`),
  },
  // Chat endpoints
  chat: {
    getChatHistory: (legacyId: string) => api.get(`/legacy/${legacyId}/chat`),
    sendMessage: (legacyId: string, message: string) => api.post(`/legacy/${legacyId}/chat`, { message }),
  },
  // Media endpoints
  media: {
    getMedias: (legacyId: string, params: Record<string, any> = {}) =>
      api.get<ResponsePagination<IMedia[]>>(`/media`, { params: { assistant_id: legacyId, ...params } }),
    uploadFile: (formData: any) =>
      api.post<BaseResponse<IMedia> | ValidationError | GenericError>("/media", formData, configMedia),
    deleteFile: (fileId: string) => api.delete(`/media/${fileId}`),
  },
  // Sessions
  sessions: {
    start: (payload: LegacyTypes.ISessionStartPayload) =>
      api.post<BaseResponse<IOpenSession> | ValidationError | GenericError>(`/sessions/start`, payload),
    end: (sessionId: string) =>
      api.post<BaseResponse<IOpenSession> | ValidationError | GenericError>(`/sessions/${sessionId}/end`),
  },
  // Interactions
  interactions: {
    getCollection: (params: ChatTypes.IQueryParamsChat) => httpChat.get("/messages", { params }),
    create: (payload: ChatTypes.IPayloadCreate) => httpChat.post("/messages", payload, configMedia),
    cancel: (uuid: ChatTypes.IInteraction["id"]) => api.post(`/interactions/${uuid}/cancel`),
    delete: (id: ChatTypes.IChat["_id"]) => httpChat.delete(`/messages/${id}`),
    latest: () => httpChat.get(`/messages/latest`),
    // Whatsapp endpoints
    whatsappImport: (payload: FormData) => httpChat.post("/whatsapp/import", payload, configMedia),
    whatsappVerify: (payload: FormData) => httpChat.post("/whatsapp/verify", payload, configMedia),
  },
  users: {
    getCollection: (params: Record<string, any>) =>
      api.get<UsersTypes.UsersResponse>("/admin-administrate-users", { params }),
  },
}
